import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import {
	CreateOpportunity,
	CreateOpportunityResponse,
	GeneralTag,
	InteractionDetailsResponse,
	InteractionSearch,
	InteractionSearchResponse,
	OpportunitiesResponse,
	OpportunityDTO,
	Process,
	SearchOpportunity,
	StateOpportunity,
	TagsAssignedOppportunities,
	UpdateOpportunity
} from '@interfaces'
import { BehaviorSubject, Observable, Subject, distinctUntilChanged } from 'rxjs'

@Injectable({
	providedIn: 'root'
})
export class OpportunityService {
	private http = inject(HttpClient)
	url = '/opportunities/opportunities'

	// Observable para guardar informacion del processo y las etapas
	private _lastProcessSubject = new BehaviorSubject<Process | null>(null)
	lastProcess$ = this._lastProcessSubject.asObservable()

	// Observable para la edición de oportunidad en detalles
	private _stateOpportunitiesSubject = new BehaviorSubject<StateOpportunity>('PENDING')
	stateOpportunities$ = this._stateOpportunitiesSubject.asObservable()

	// Observable para manejar los detalles de una oportunidad
	opportunityDetailsSubject = new BehaviorSubject<OpportunityDTO | null>(null)
	$opportunityDetails = this.opportunityDetailsSubject.asObservable()

	// Observable para manejar el filtro de las menciones en detalles de una oportunidad
	inputMentionFilter = new Subject<string>()
	$inputMentionFilter = this.inputMentionFilter.asObservable()

	// Cambia el stage de una opportunidad
	changeOpportunityStage(opportunityId: number, stageId: number) {
		return this.http.put<void>(`${this.url}/${opportunityId}?idStage=${stageId}`, {})
	}

	// Cambia el estado de una oportunidad
	changeOpportunityState(
		state: StateOpportunity,
		opportunityId: number,
		resumptionDate?: Date
	): Observable<OpportunityDTO> {
		let url = `${this.url}/${opportunityId}?state=${state}`

		if (resumptionDate) {
			url += `&resumptionDate=${resumptionDate.toISOString()}`
		}

		return this.http.put<OpportunityDTO>(url, {})
	}

	// Método para crear oportunidades
	createOpportunity(data: CreateOpportunity): Observable<CreateOpportunityResponse> {
		return this.http.post<CreateOpportunityResponse>(`${this.url}`, data)
	}

	// Elimina una etiqueta de una oportunidad
	deleteAssignedTags(opportunityId: number, tagId: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${opportunityId}/tag/${tagId}`)
	}

	// Elimina una empresa vinculada con una oportunidad en concreto
	deleteLinkedEnterprise(opportunityId: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${opportunityId}/enterprise`)
	}

	// Elimina un oportunidad
	deleteOpportunity(opportunityList: { id: number }[]): Observable<void> {
		return this.http.delete<void>(`${this.url}`, { body: opportunityList })
	}

	// Devuelve un blob para descargar el excel con los oportunidades seleccionados
	exportOpportunities(ids: Set<number>): Observable<HttpResponse<Blob>> {
		const selectedOpportunities = ids.size ? { ids: Array.from(ids) } : {}
		return this.http.post<Blob>(`${this.url}/export`, selectedOpportunities, {
			observe: 'response',
			responseType: 'blob' as 'json'
		})
	}

	// Obtiene los detalles de una interacción
	getInteractionDetails(
		opportunityId: number,
		interactionId: number,
		page: number = 0,
		size: number = 10
	): Observable<InteractionDetailsResponse> {
		return this.http.get<InteractionDetailsResponse>(
			`${this.url}/${opportunityId}/interactions/${interactionId}/detail?page=${page}&size=${size}`
		)
	}

	// Devuelve los oportunidades segun las opciones que vengan por parametro
	getOpportunities(data: SearchOpportunity): Observable<OpportunitiesResponse> {
		return this.http.post<OpportunitiesResponse>(`${this.url}/search`, data)
	}

	// Devuelve el detalle del contacto
	getOpportunityDetails(opportunityId: number): Observable<OpportunityDTO> {
		return this.http.get<OpportunityDTO>(`${this.url}/${opportunityId}`)
	}

	// Devuelve las etiquetas segun las oportunidades seleccionadas
	getOppportunitiesTags(opportunitiesId: { id: number }[]): Observable<GeneralTag[]> {
		return this.http.post<GeneralTag[]>(`${this.url}/tags/get`, opportunitiesId)
	}

	// Cambia el estado de varias oportunidades para gestion masiva
	modifyOpportunitiesState(
		state: StateOpportunity,
		opportunitiesId: { id: number }[],
		resumptionDate?: Date
	): Observable<void> {
		let url = `${this.url}/modify?state=${state}`

		if (resumptionDate) {
			url += `&resumptionDate=${resumptionDate.toISOString()}`
		}

		return this.http.post<void>(url, opportunitiesId)
	}

	// Cambia la etapa de varias oportunidades para gestion masiva
	modifyOpportunitiesStage(idStage: number, opportunitiesId: { id: number }[]): Observable<void> {
		return this.http.post<void>(`${this.url}/modify?idStage=${idStage}`, opportunitiesId)
	}

	// Cambia el proceso de varias oportunidades para gestion masiva
	modifyOpportunitiesProcess(idProcess: number, opportunitiesId: { id: number }[]): Observable<void> {
		return this.http.post<void>(`${this.url}/modify?idProcess=${idProcess}`, opportunitiesId)
	}

	// Reasigna un oportunidad a un usuario
	reassignOpportunity(newUserId: number, opportunityId: number) {
		return this.http.put<void>(`${this.url}/${opportunityId}?idUser=${newUserId}`, {})
	}

	// Setea el proceso seleccionado realizando la peticion de las oportunidades
	setLastProcess(process: Process, search?: SearchOpportunity) {
		const state = this._stateOpportunitiesSubject.getValue()

		this.getOpportunities({ ...search, idProcess: process?.id, status: state }).subscribe(
			(opportunitiesResponse: OpportunitiesResponse) => {
				process?.stages.forEach(stage => {
					const opportunitiesInStage = opportunitiesResponse.content.filter(
						opportunity => opportunity.idProcessStage === stage.id
					)
					stage.opportunities = opportunitiesInStage
				})
			}
		)

		this._lastProcessSubject.next(process)
	}

	// Setea el proceso seleccionado realizando la peticion de las oportunidades
	setInitialLastProcess() {
		const state = this._stateOpportunitiesSubject.getValue()
		const process = this._lastProcessSubject.getValue()

		this.getOpportunities({ idProcess: process?.id, status: state })
			.pipe(distinctUntilChanged())
			.subscribe((opportunitiesResponse: OpportunitiesResponse) => {
				process?.stages.forEach(stage => {
					const opportunitiesInStage = opportunitiesResponse.content.filter(
						opportunity => opportunity.idProcessStage === stage.id
					)
					stage.opportunities = opportunitiesInStage
				})
			})
		this._lastProcessSubject.next(process)
	}

	// Devuelve el listado paginado de una búsqueda de interacciones
	searchOpportunityInteractions(search: InteractionSearch): Observable<InteractionSearchResponse> {
		return this.http.post<InteractionSearchResponse>(`${this.url}/interactions/search`, search)
	}

	// Reasigna un oportunidad a un usuario
	updateOpportunity(opportunityId: number, paramsBody: UpdateOpportunity): Observable<OpportunityDTO> {
		const params = new HttpParams({ fromObject: paramsBody as any })
		return this.http.put<OpportunityDTO>(`${this.url}/${opportunityId}`, null, { params })
	}

	// Reseta el observable de oportunidades
	resetOpportunityDetail() {
		this.opportunityDetailsSubject.next(null)
	}

	// Actualiza el filtro de las menciones
	setInputMentionFilter(value: string = ''): void {
		this.inputMentionFilter.next(value)
	}

	// Cambia el valor del observable de filtrar por un valor segun el estado seleccionado
	setOpportunityState(state: StateOpportunity) {
		const process = this._lastProcessSubject.getValue()
		this._stateOpportunitiesSubject.next(state)
		this.setLastProcess(process!)
	}

	// Actualiza las etiquetas de las oportunidades seleccionadas
	updateAssignedTags(data: TagsAssignedOppportunities): Observable<void> {
		return this.http.post<void>(`${this.url}/tags/edit`, data)
	}
}
